'use client';

import React from 'react';

import { Box, BoxProps } from './box';
import { Text } from './text';
import { Heading } from './heading';

// Card Root Component
export interface CardRootProps extends BoxProps {
  variant?: 'default' | 'outline' | 'elevated' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  children: React.ReactNode;
}

const CardRoot = React.forwardRef<HTMLDivElement, CardRootProps>(
  ({ variant = 'default', size = 'md', children, ...props }, ref) => {
    const variants: Record<string, Record<string, string>> = {
      default: {
        backgroundColor: 'white',
        border: '1px solid #E2E8F0',
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
      },
      outline: {
        backgroundColor: 'transparent',
        border: '1px solid #E2E8F0',
      },
      elevated: {
        backgroundColor: 'white',
        border: '1px solid #E2E8F0',
        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
      },
      ghost: {
        backgroundColor: 'transparent',
        border: 'none',
      },
    };

    const sizes: Record<string, Record<string, string>> = {
      sm: {
        borderRadius: 'md',
      },
      md: {
        borderRadius: 'lg',
      },
      lg: {
        borderRadius: 'xl',
      },
    };

    return (
      <Box ref={ref} overflow="hidden" {...variants[variant]} {...sizes[size]} {...props}>
        {children}
      </Box>
    );
  }
);

CardRoot.displayName = 'CardRoot';

// Card Header Component
export interface CardHeaderProps extends BoxProps {
  children: React.ReactNode;
}

const CardHeader = React.forwardRef<HTMLDivElement, CardHeaderProps>(
  ({ children, ...props }, ref) => {
    return (
      <Box ref={ref} padding={6} paddingBottom={3} {...props}>
        {children}
      </Box>
    );
  }
);

CardHeader.displayName = 'CardHeader';

// Card Body Component
export interface CardBodyProps extends BoxProps {
  children: React.ReactNode;
}

const CardBody = React.forwardRef<HTMLDivElement, CardBodyProps>(({ children, ...props }, ref) => {
  return (
    <Box ref={ref} padding={6} paddingTop={0} {...props}>
      {children}
    </Box>
  );
});

CardBody.displayName = 'CardBody';

// Card Footer Component
export interface CardFooterProps extends BoxProps {
  children: React.ReactNode;
}

const CardFooter = React.forwardRef<HTMLDivElement, CardFooterProps>(
  ({ children, ...props }, ref) => {
    return (
      <Box ref={ref} padding={6} paddingTop={3} borderTop="1px solid #E2E8F0" {...props}>
        {children}
      </Box>
    );
  }
);

CardFooter.displayName = 'CardFooter';

// Card Title Component
export interface CardTitleProps {
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const CardTitle = React.forwardRef<HTMLHeadingElement, CardTitleProps>(
  ({ children, size = 'md', className }, ref) => {
    const sizeMap = {
      sm: 'sm',
      md: 'md',
      lg: 'lg',
    };

    return (
      <Heading
        ref={ref}
        size={sizeMap[size] as 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl'}
        lineHeight="1.2"
        marginBottom={1}
        className={className}
      >
        {children}
      </Heading>
    );
  }
);

CardTitle.displayName = 'CardTitle';

// Card Description Component
export interface CardDescriptionProps extends BoxProps {
  children: React.ReactNode;
}

const CardDescription = React.forwardRef<HTMLDivElement, CardDescriptionProps>(
  ({ children, ...props }, ref) => {
    return (
      <Text ref={ref} fontSize="sm" color="#6B7280" lineHeight="1.4" {...props}>
        {children}
      </Text>
    );
  }
);

CardDescription.displayName = 'CardDescription';

// Card Content Component (same as CardBody but for backward compatibility)
export interface CardContentProps extends BoxProps {
  children: React.ReactNode;
}

const CardContent = React.forwardRef<HTMLDivElement, CardContentProps>(
  ({ children, ...props }, ref) => {
    return (
      <Box ref={ref} padding={6} paddingTop={0} {...props}>
        {children}
      </Box>
    );
  }
);

CardContent.displayName = 'CardContent';

// Compound component exports
export const Card = {
  Root: CardRoot,
  Header: CardHeader,
  Body: CardBody,
  Footer: CardFooter,
  Title: CardTitle,
  Description: CardDescription,
  Content: CardContent,
};

// Individual exports for direct usage
export { CardRoot, CardHeader, CardBody, CardFooter, CardTitle, CardDescription, CardContent };

// Default Card export (non-compound version)
export type CardProps = CardRootProps;

const CardComponent = React.forwardRef<HTMLDivElement, CardProps>((props, ref) => (
  <CardRoot ref={ref} {...props} />
));

CardComponent.displayName = 'Card';

export default CardComponent;
